/* eslint-disable react/no-danger */
import * as React from 'react';

import uniqBy from 'lodash/uniqBy';

import type { PartnerPageSchemaPropertyValue } from 'bundles/partners-graphql/components/cds/partnerPageStatsUtils';
import { clearEmptyFields } from 'bundles/seo/utils/utils';

type Props = {
  additionalProperties?: PartnerPageSchemaPropertyValue[];
  partner: {
    shortName: string;
    name: string;
    description: string | null;
    squareLogo: string | null;
    links: {
      website: string | null;
      twitter: string | null;
      facebook: string | null;
      youtube: string | null;
    } | null;
    specializations: {
      elements: ({ __typename: 'OnDemandSpecializationsV1'; id: string; slug: string } | null)[];
    } | null;
    courses: { elements: ({ __typename: 'CoursesV1'; id: string; slug: string } | null)[] } | null;
  };
};

const PartnerPageGoogleSchemaMarkup = ({ additionalProperties, partner }: Props) => {
  const partnerPagePath = `/partners/${partner.shortName}`;

  // Get unique list of items using the id field to check if item already exists in the array
  const items = uniqBy([...(partner.specializations?.elements || []), ...(partner.courses?.elements || [])], 'id');
  const markupJson = {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    Name: `${partner.name} | Coursera`,
    url: `https://www.coursera.org${partnerPagePath}`,
    about: {
      '@id': `https://www.coursera.org${partnerPagePath}#institution`,
      '@type': 'CollegeOrUniversity',
      name: partner.name,
      description: partner.description,
      logo: `${partner.squareLogo}`,
      url: `https://www.coursera.org${partnerPagePath}`,
      sameAs: Object.values(partner.links || {}),
      ...(additionalProperties?.length
        ? {
            additionalProperty: additionalProperties,
          }
        : {}),
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: items
        .map((item, i) => {
          if (!item) return null;
          return item.__typename === 'CoursesV1'
            ? {
                '@type': 'ListItem',
                position: i + 1,
                '@id': `https://www.coursera.org/learn/${item.slug}#mooc`,
                url: `https://www.coursera.org/learn/${item.slug}`,
              }
            : {
                '@type': 'ListItem',
                position: i + 1,
                '@id': `https://www.coursera.org/specializations/${item.slug}#specialization`,
                url: `https://www.coursera.org/specializations/${item.slug}`,
              };
        })
        .filter((item) => item !== null),
    },
  };

  const createMarkup = () => ({
    __html: `
        <script type="application/ld+json">
          ${JSON.stringify(clearEmptyFields(markupJson))}
        </script>
      `,
  });

  return (
    <div className="rc-PartnerPageGoogleSchemaMarkup">
      <div dangerouslySetInnerHTML={createMarkup()} />
    </div>
  );
};

export default PartnerPageGoogleSchemaMarkup;
